import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight, Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function SubmissionSuccess() {
  const { state } = useLocation();
  const trackerUrl = state?.tracker_url;
  const trackerToken = state?.tracker_token;
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    if (!trackerToken) return;
    api.get(`/track/${trackerToken}/qr`)
      .then(({ data }) => setQr(data.data_url))
      .catch(() => {});
  }, [trackerToken]);

  const copy = async () => {
    if (!trackerUrl) return;
    try {
      await navigator.clipboard.writeText(trackerUrl);
      setCopied(true);
      toast.success("Tracker link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center" data-testid="submission-success">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-success" strokeWidth={1.5} />
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-3">Application received</div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-4">
          Thank you. We're on it.
        </h1>
        <p className="text-sage leading-relaxed mb-8">
          Your application has been routed to one of our licensed agents. You'll hear from us within 24 hours
          via email or phone with next steps.
        </p>

        {trackerUrl && (
          <div className="bg-white border border-line rounded-sm p-5 mb-8 text-left" data-testid="tracker-card">
            <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-2">Your tracking link</div>
            <p className="text-sm text-sage mb-3">
              Bookmark this link or scan the QR code to check your application status anytime — no login needed.
            </p>
            {qr && (
              <div className="flex justify-center mb-3">
                <img
                  src={qr}
                  alt="Tracker QR"
                  className="w-40 h-40 bg-white p-2 border border-line rounded-sm"
                  data-testid="tracker-qr-image"
                />
              </div>
            )}
            <div className="flex items-center gap-2 bg-cream/60 border border-line rounded-sm px-3 py-2.5 mb-3">
              <span className="text-xs font-mono text-forest truncate flex-1" data-testid="tracker-url">{trackerUrl}</span>
              <button
                onClick={copy}
                className="text-clay hover:text-claydark transition-colors duration-200 flex-shrink-0"
                data-testid="copy-tracker-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <a href={trackerUrl} target="_blank" rel="noreferrer" data-testid="open-tracker-link">
              <Button variant="outline" className="w-full border-forest text-forest hover:bg-forest hover:text-bone rounded-sm h-10">
                Open tracker <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        )}

        <Link to="/" data-testid="back-home-btn">
          <Button className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6">
            Back to home <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
